import React, {useEffect, useRef, useState} from "react"
import {CircularProgress, Stack, Typography} from "@mui/joy"
import {useTranslation} from "react-i18next"
import Input from "./Input"
import TeamSearchComponent from "./TeamSearchComponent"
import {useApiService} from "../hooks/useApiService"
import {getTeams} from "../services/api/team"
import {TeamPreview, TeamResponse} from "../types/team"

export type TeamSearchFieldProps = {
    placeholder?: string
    /** Team currently picked with this field, used to keep the input in step with it */
    selectedTeam?: TeamPreview
    onSelect: (team: TeamPreview) => void
}

/**
 * Long enough that a word typed in one go costs a single request.
 *
 * The provider plan is counted in requests per day and every distinct term is one of them, so a
 * short delay turned "arsenal" into a request for "ars", "arse", "arsen" and so on.
 */
const SEARCH_DEBOUNCE_MS = 700

/** The search endpoint ignores anything shorter, so it is not worth a request */
const MIN_SEARCH_LENGTH = 3

/**
 * Team search box, holding its own query.
 *
 * Typing used to live in the page state, so every keystroke re-rendered the analysis, the fixture
 * list and the other field along with it, and the input lagged behind the typing. Keeping the query
 * here means a keystroke only re-renders this field, and the page only hears about a pick.
 */
const TeamSearchField = ({placeholder, selectedTeam, onSelect}: TeamSearchFieldProps) => {
    const apiService = useApiService()
    const {t} = useTranslation()
    const [query, setQuery] = useState(selectedTeam?.name || "")
    const [results, setResults] = useState<TeamResponse[]>([])
    const [loading, setLoading] = useState(false)
    /** Why a search came back with nothing, when it is not simply that nothing matched */
    const [error, setError] = useState("")
    const [open, setOpen] = useState(false)
    const debounceRef = useRef<ReturnType<typeof setTimeout>>(null)
    /** Bumped per search so a slow answer cannot overwrite the results of a later one */
    const runRef = useRef(0)
    /**
     * Name written into the input by a pick.
     *
     * Held here rather than read from the selected team, so the field does not depend on the parent
     * feeding the selection back to know it must not search for what it just filled in.
     */
    const pickedName = useRef(selectedTeam?.name)

    /**
     * A team picked outside of this field — filling in the sides of a fixture picked from the list —
     * has to show up in the input as if it had been typed and picked here.
     */
    useEffect(() => {
        if (!selectedTeam?.name || selectedTeam.name === pickedName.current) return

        clearTimeout(debounceRef.current)
        runRef.current += 1
        pickedName.current = selectedTeam.name

        setQuery(selectedTeam.name)
        setResults([])
        setLoading(false)
        setOpen(false)
    }, [selectedTeam?.name])

    useEffect(() => {
        const value = query.trim()

        // Picking a team fills the input with its name, which is not a search worth running
        if (value.length < MIN_SEARCH_LENGTH || value === pickedName.current || value === selectedTeam?.name) {
            setResults([])
            setLoading(false)
            return
        }

        setLoading(true)
        setError("")
        debounceRef.current = setTimeout(async () => {
            const run = ++runRef.current

            try {
                const client = await apiService.client(true, false)
                const response = await getTeams(client, {search: value})

                if (run !== runRef.current) return
                setResults(response?.response || [])
            } catch (requestError) {
                /**
                 * The search is the only way into the app, so a failed one says so rather than
                 * leaving an empty list that reads as "this club does not exist".
                 */
                if (run === runRef.current) {
                    setResults([])
                    setError(requestError?.["message"] || t("teamSearchUnavailable"))
                }
            } finally {
                if (run === runRef.current) setLoading(false)
            }
        }, SEARCH_DEBOUNCE_MS)

        return () => clearTimeout(debounceRef.current)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query, selectedTeam?.name])

    const select = (team: TeamPreview) => {
        // Stops the pending debounce from searching for the name that was just picked
        clearTimeout(debounceRef.current)
        runRef.current += 1
        pickedName.current = team.name

        setQuery(team.name)
        setResults([])
        setError("")
        setLoading(false)
        setOpen(false)
        onSelect(team)
    }

    const searched = query.trim().length >= MIN_SEARCH_LENGTH && query.trim() !== pickedName.current

    return <Stack gap={0.5}>
        <TeamSearchComponent searchBarResultsOpen={open && !!results.length}
                             onCloseSearch={() => setOpen(false)}
                             onSelectItem={select}
                             searchResult={results}
                             anchor={<Input placeholder={placeholder}
                                            value={query}
                                            onChange={event => {
                                                setQuery(event.target.value)
                                                setOpen(true)
                                            }}
                                            onFocus={() => setOpen(true)}
                                            endDecorator={loading && <CircularProgress size={"sm"}/>}/>}/>

        {error
            ? <Typography level={"body-xs"} color={"danger"}>{error}</Typography>
            : searched && !loading && !results.length &&
            <Typography level={"body-xs"} textColor={"text.tertiary"}>{t("teamSearchNoResult")}</Typography>}
    </Stack>
}

export default TeamSearchField
